import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  RefObject,
  useCallback,
} from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { observer } from "mobx-react";
import ProductArrivalModal from "common/ProductArrivalModal/ProductArrivalModal";
import { RegularCardProduct } from "common/ProductPreview/components/Regular/types";
import { useStore } from "store";
import { useYandexVendor } from "services/YandexMetricaApiService";
import {
  useExperiment,
  useImage,
  useLunaTrack,
  useProductPrices,
} from "helpers/hooks";
import { useOnWatch } from "helpers/analytics";
import {
  EX_NEW_BUTTON_TEXT,
  EX_NEW_PRICE_PREFIX,
  IMAGE_SIZE_120_120,
  IMAGE_SIZE_200_200,
} from "constants/variables";
import Regular from "./components/Regular/Regular";
import HorizontalSmall from "./components/HorizontalSmall/HorizontalSmall";
import Horizontal from "./components/Horizontal/Horizontal";
import RegularSkeleton from "./components/RegularSkeleton/RegularSkeleton";
import HorizontalSmallSkeleton from "./components/HorizontalSmallSkeleton/HorizontalSmallSkeleton";

export type ProductPreviewType =
  | "horizontal"
  | "horizontalSmall"
  | "horizontalSmallSkeleton"
  | "regularSkeleton"
  | "regular";

type RegularCardProps = { type: "regular"; product: RegularCardProduct };

export type Props =
  | {
      bg?: string;
      onWatch?: Entity.Analytics.OnWatch;
      onClick?: (value: { product: Entity.Product }) => void;
      index?: number;
      ellipsis?: number;
      fade?: boolean;
      hideCartToast?: boolean;
      disableLazyImage?: boolean;
      withSKU?: boolean;
      analyticsMeta?: Entity.ProductAnalyticsMeta;
      enableCustomPrefixPrice?: boolean;
      showExpiringPrices?: boolean;
      isgPriceDefault?: boolean;
    } & (
      | {
          type?: ProductPreviewType;
          product: Entity.Product;
        }
      | RegularCardProps
    );

export type CommonProps = {
  onInteract: (event?: React.MouseEvent) => Promise<void>;
  analyticsMeta?: Entity.ProductAnalyticsMeta;
  imageSrc: ReturnType<typeof useImage>;
  ref: RefObject<HTMLDivElement>;
  onOpenNotify: () => void;
  pricePrefix: string | undefined;
  addCartText: string;
  showReviewCount?: Common.UnionCookie;
  isEmployeeMode: boolean;
  prices: Array<Entity.Product.Price>;
  price: Entity.Product.Price;
  isNewPricePrefix: boolean;
};

const ProductPreview: React.FC<Props> = ({ type = "regular", ...props }) => {
  const { onImps, onTrack, hasImps, hasTracks } = useLunaTrack({
    product: props.product,
    analyticsMeta: props.analyticsMeta,
  });
  const productRef = useRef(null);
  const { pageview } = useYandexVendor();
  const { notifications, user } = useStore();
  const [pricePrefix, setPricePrefix] = useState(undefined);
  const [addCartText, setAddCartText] = useState("В корзину");
  const { product, onWatch, showExpiringPrices, isgPriceDefault } = props;
  const prices = useProductPrices(props.product, showExpiringPrices);

  const exNewPricePrefix = useFeatureIsOn("ex_new_priceprefix");
  const exNewButtonText = useFeatureIsOn("ex_new_buttontext");

  const inStock = prices?.length > 1 && showExpiringPrices;

  const onWatchProductPreview = useCallback(
    data => {
      const { subject = null } = data;

      if (subject) {
        pageview(subject);
      }

      return hasImps ? onImps() : onWatch && onWatch(data);
    },
    [hasImps, onImps, pageview, onWatch]
  );

  useOnWatch({
    subject: {
      ...product,
      inStock,
    },
    onWatch: onWatchProductPreview,
    index: props.index,
    elementRef: productRef,
  });

  const imageSrc = useImage(product?.image, {
    mobile: IMAGE_SIZE_120_120,
    tablet: IMAGE_SIZE_200_200,
  });

  //договорились с Евгением Конкином что используем проперти из продукта и гравбух(вместо оптимайзера) вместе - оносительно того как было раньше
  const isNewPricePrefix =
    props.enableCustomPrefixPrice && exNewPricePrefix && product?.seoPriceText;
  const isNewButtonText =
    props.enableCustomPrefixPrice && exNewButtonText && product?.seoBasketText;

  useEffect(() => {
    if (isNewButtonText) {
      setAddCartText(EX_NEW_BUTTON_TEXT);
    }
  }, [isNewButtonText]);

  useEffect(() => {
    if (isNewPricePrefix) {
      setPricePrefix(EX_NEW_PRICE_PREFIX);
    }
  }, [isNewPricePrefix]);

  const openArrivalModal = (): void => {
    notifications.show({
      type: "modal",
      component: ProductArrivalModal,
      props: {
        productId: product.id,
      },
    });
  };

  const onOpenNotify = () =>
    user.withAuth({
      callback: openArrivalModal,
      props: {
        onLogin: openArrivalModal,
      },
    });

  const exReviewCounter = useExperiment("ex_review_counter");

  const price = useMemo(() => {
    if (type !== "horizontal" && user.isEmployeeMode) {
      return prices?.sort((a, b) => a.price - b.price)[0];
    }

    if (inStock && isgPriceDefault && !user.isEmployeeMode) {
      return prices.find(price => price.isExpirating) || prices[0];
    }

    return prices[0];
  }, [prices, type, user.isEmployeeMode, inStock, isgPriceDefault]);

  const other: CommonProps = {
    onInteract: onTrack,
    imageSrc,
    ref: productRef,
    onOpenNotify,
    pricePrefix,
    addCartText,
    showReviewCount: exReviewCounter,
    isEmployeeMode: user.isEmployeeMode,
    prices,
    price,
    isNewPricePrefix,
  };

  switch (type) {
    case "horizontal":
      return <Horizontal {...props} {...other} />;
    case "horizontalSmall":
      return <HorizontalSmall {...props} {...other} />;
    case "horizontalSmallSkeleton":
      return <HorizontalSmallSkeleton />;
    case "regularSkeleton":
      return <RegularSkeleton />;
    case "regular":
    default:
      return <Regular {...props} {...other} />;
  }
};

ProductPreview.displayName = "ProductPreview";

export default observer(ProductPreview);
